/* NOCTURNE — EaziSleep theme JS. Vanilla, dependency-free. */
(() => {
  'use strict';
  const N = window.Nocturne || { settings: {}, routes: {}, strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  const formatMoney = (cents) => {
    const fmt = N.moneyFormat || '{{amount}}';
    const amount = (cents / 100).toFixed(2);
    const whole = Math.round(cents / 100).toLocaleString();
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/, Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 }))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, whole)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, whole);
  };

  /* ---------- Scroll reveal ---------- */
  const initReveal = () => {
    const targets = $$('[data-reveal], [data-reveal-group]');
    if (!targets.length) return;
    if (reduceMotion || N.settings.reveal === false || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    $$('[data-reveal-group]').forEach((group) => {
      [...group.children].forEach((child, i) => child.style.setProperty('--stagger-i', i));
    });
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('is-revealed'); io.unobserve(e.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    targets.forEach((el) => io.observe(el));
  };

  /* ---------- Header scroll state ---------- */
  const initHeader = () => {
    const header = $('.site-header');
    if (!header) return;
    let ticking = false;
    const update = () => { header.classList.toggle('is-scrolled', window.scrollY > 12); ticking = false; };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  };

  /* ---------- Announcement rotator ---------- */
  const initAnnouncement = () => {
    const items = $$('.announcement__item');
    if (items.length < 2) return;
    let i = 0;
    setInterval(() => {
      items[i].classList.remove('is-active');
      i = (i + 1) % items.length;
      items[i].classList.add('is-active');
    }, 4000);
  };

  /* ---------- Drawers (nav + cart) ---------- */
  const drawers = {};
  const trapFocus = (panel, e) => {
    const focusables = $$('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])', panel)
      .filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0]; const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  };
  const setupDrawer = (id) => {
    const root = document.getElementById(id);
    if (!root) return null;
    const panel = $('[data-drawer-panel]', root);
    let lastFocus = null;
    const api = {
      open() {
        lastFocus = document.activeElement;
        root.classList.add('is-open');
        root.setAttribute('aria-hidden', 'false');
        document.body.classList.add('drawer-open');
        const closeBtn = $('[data-drawer-close]', root);
        if (closeBtn) closeBtn.focus();
      },
      close() {
        root.classList.remove('is-open');
        root.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('drawer-open');
        if (lastFocus) lastFocus.focus();
      },
      get isOpen() { return root.classList.contains('is-open'); }
    };
    $$('[data-drawer-close]', root).forEach((b) => b.addEventListener('click', api.close));
    const scrim = $('[data-drawer-scrim]', root);
    if (scrim) scrim.addEventListener('click', api.close);
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') api.close();
      if (e.key === 'Tab' && api.isOpen && panel) trapFocus(panel, e);
    });
    return api;
  };
  const initDrawers = () => {
    drawers.nav = setupDrawer('NavDrawer');
    drawers.cart = setupDrawer('CartDrawer');
    $$('[data-open-nav]').forEach((b) => b.addEventListener('click', () => drawers.nav && drawers.nav.open()));
    $$('[data-open-cart]').forEach((b) => b.addEventListener('click', (e) => {
      if (N.cartType === 'drawer' && drawers.cart) { e.preventDefault(); drawers.cart.open(); }
    }));
  };

  /* ---------- Toast ---------- */
  let toastEl; let toastTimer;
  const toast = (msg) => {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'toast';
      toastEl.setAttribute('role', 'status');
      toastEl.innerHTML = '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="m4.5 12.5 5 5 10-11"/></svg><span></span>';
      document.body.appendChild(toastEl);
    }
    $('span', toastEl).textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), 2600);
  };

  /* ---------- Cart API ---------- */
  const refreshCartUI = async () => {
    try {
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const sections = await res.json();
      const html = sections['cart-drawer'];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.getElementById('CartDrawer');
        const current = document.getElementById('CartDrawer');
        if (fresh && current) {
          const wasOpen = current.classList.contains('is-open');
          current.innerHTML = fresh.innerHTML;
          if (wasOpen) current.classList.add('is-open');
          drawers.cart = setupDrawer('CartDrawer');
          bindCartLines();
        }
      }
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.dataset.count = cart.item_count;
      });
    } catch (e) { /* silent */ }
  };

  const cartAdd = async (body, btn) => {
    if (btn) btn.classList.add('is-loading');
    try {
      const res = await fetch(N.routes.cartAdd + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json();
        toast(err.description || err.message || 'Could not add to cart');
        return false;
      }
      await refreshCartUI();
      if (N.cartType === 'drawer' && drawers.cart) drawers.cart.open();
      else toast(N.strings.added || 'Added to cart');
      return true;
    } catch (e) {
      toast('Network error — please try again');
      return false;
    } finally {
      if (btn) btn.classList.remove('is-loading');
    }
  };

  const cartChangeLine = async (line, quantity) => {
    await fetch(N.routes.cartChange + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });
    await refreshCartUI();
    // If on cart page, reload to sync totals
    if (document.body.classList.contains('template-cart')) window.location.reload();
  };

  // Optimistic quantity changes: input updates instantly, server syncs after a pause
  const qtyTimers = {};
  const queueCartChange = (line, quantity) => {
    clearTimeout(qtyTimers[line]);
    qtyTimers[line] = setTimeout(() => cartChangeLine(line, quantity), 450);
  };

  const bindCartLines = () => {
    $$('[data-cart-line]').forEach((row) => {
      const line = Number(row.dataset.cartLine);
      const input = $('input[type="number"]', row);
      const bump = (delta) => {
        const next = Math.max(0, (Number(input.value) || 0) + delta);
        input.value = next;                 // instant feedback
        queueCartChange(line, next);        // debounced sync
      };
      $$('[data-qty-plus]', row).forEach((b) => b.addEventListener('click', () => bump(1)));
      $$('[data-qty-minus]', row).forEach((b) => b.addEventListener('click', () => bump(-1)));
      $$('[data-line-remove]', row).forEach((b) => b.addEventListener('click', () => { clearTimeout(qtyTimers[line]); cartChangeLine(line, 0); }));
      if (input) input.addEventListener('change', () => queueCartChange(line, Math.max(0, Number(input.value) || 0)));
    });
  };

  /* ---------- Product forms (ATC buttons everywhere) ---------- */
  const initProductForms = () => {
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[data-ajax-atc]');
      if (!form) return;
      e.preventDefault();
      const btn = $('[type="submit"]', form);
      const fd = new FormData(form);
      const id = Number(fd.get('id'));
      // If multiple quantity inputs exist, trust the last (visible stepper) one
      const qtyValues = fd.getAll('quantity');
      const quantity = Number(qtyValues[qtyValues.length - 1] || 1);
      if (!id) return;
      const props = {};
      fd.forEach((v, k) => {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = v;
      });
      cartAdd({ items: [{ id, quantity, properties: Object.keys(props).length ? props : undefined }] }, btn);
    });
    // Simple one-tap add buttons
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-quick-add]');
      if (!btn) return;
      e.preventDefault();
      const id = Number(btn.dataset.quickAdd);
      const qty = Number(btn.dataset.quickAddQty || 1);
      if (id) cartAdd({ items: [{ id, quantity: qty }] }, btn);
    });
  };

  /* ---------- Pack selector (PDP + bundles section) ---------- */
  const initPackSelectors = () => {
    $$('[data-pack-select]').forEach((wrap) => {
      const options = $$('.pack-option', wrap);
      const form = wrap.closest('form') || $(wrap.dataset.formTarget || 'form[data-ajax-atc]');
      const priceOut = $$('[data-pack-price]');
      const stickyPrice = $$('[data-sticky-price]');
      const select = (opt) => {
        options.forEach((o) => {
          const on = o === opt;
          o.classList.toggle('is-selected', on);
          const input = $('input', o);
          if (input) input.checked = on;
        });
        if (form) {
          const idInput = $('input[name="id"]', form);
          if (idInput) idInput.value = opt.dataset.variantId;
          const qtyInput = $('input[name="quantity"]', form);
          if (qtyInput) qtyInput.value = opt.dataset.quantity || 1;
        }
        const cents = Number(opt.dataset.price);
        if (!Number.isNaN(cents)) {
          priceOut.forEach((el) => { el.textContent = formatMoney(cents); });
          stickyPrice.forEach((el) => { el.textContent = formatMoney(cents); });
        }
      };
      options.forEach((o) => {
        o.addEventListener('click', () => select(o));
        o.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); select(o); } });
      });
      const initial = options.find((o) => o.classList.contains('is-selected')) || options[0];
      if (initial) select(initial);
    });
  };

  /* ---------- PDP gallery ---------- */
  const initGallery = () => {
    const gallery = $('[data-gallery]');
    if (!gallery) return;
    const slides = $$('.pdp-gallery__slide', gallery);
    const thumbs = $$('.pdp-gallery__thumb', gallery);
    const show = (i) => {
      slides.forEach((s, j) => s.classList.toggle('is-active', i === j));
      thumbs.forEach((t, j) => {
        t.classList.toggle('is-active', i === j);
        t.setAttribute('aria-current', i === j ? 'true' : 'false');
      });
    };
    thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
    let startX = 0;
    gallery.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    gallery.addEventListener('touchend', (e) => {
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) < 40) return;
      const cur = slides.findIndex((s) => s.classList.contains('is-active'));
      const next = dx < 0 ? Math.min(cur + 1, slides.length - 1) : Math.max(cur - 1, 0);
      show(next);
    }, { passive: true });
  };

  /* ---------- Sticky ATC visibility ---------- */
  const initStickyAtc = () => {
    const bar = $('.sticky-atc');
    const anchor = $('[data-atc-anchor]');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    }, { threshold: 0 });
    io.observe(anchor);
    const stickyBtn = $('[data-sticky-atc-btn]', bar);
    if (stickyBtn) stickyBtn.addEventListener('click', () => {
      const form = $('form[data-ajax-atc]');
      if (form) form.requestSubmit();
    });
  };

  /* ---------- Starfield canvas ---------- */
  const initStarfields = () => {
    if (reduceMotion || N.settings.starfield === false) return;
    $$('[data-starfield]').forEach((canvas) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      let stars = []; let meteors = []; let w = 0; let h = 0; let raf = null; let visible = false;
      let nextMeteor = 1500;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const isMobile = window.innerWidth < 750;
      const resize = () => {
        const rect = canvas.parentElement.getBoundingClientRect();
        w = rect.width; h = rect.height;
        canvas.width = w * dpr; canvas.height = h * dpr;
        canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const count = Math.min(isMobile ? 55 : 150, Math.floor((w * h) / (isMobile ? 14000 : 8000)));
        stars = Array.from({ length: count }, () => ({
          x: Math.random() * w,
          y: Math.random() * h,
          r: Math.random() * 1.3 + 0.3,
          p: Math.random() * Math.PI * 2,
          s: Math.random() * 0.6 + 0.25,
          d: Math.random() * 0.06 + 0.015 // slow drift
        }));
      };
      const spawnMeteor = () => {
        const angle = Math.PI * (0.72 + Math.random() * 0.12); // down-left diagonal
        meteors.push({
          x: w * (0.3 + Math.random() * 0.68),
          y: h * Math.random() * 0.35,
          vx: Math.cos(angle) * (7 + Math.random() * 5),
          vy: -Math.sin(angle) * (7 + Math.random() * 5) * -1,
          life: 1,
          len: 90 + Math.random() * 70
        });
      };
      const tick = (t) => {
        if (!visible) { raf = null; return; }
        ctx.clearRect(0, 0, w, h);
        for (const st of stars) {
          const a = 0.25 + 0.55 * (0.5 + 0.5 * Math.sin(t / 1000 * st.s + st.p));
          ctx.globalAlpha = a;
          ctx.fillStyle = '#DDE6FF';
          ctx.beginPath();
          ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
          ctx.fill();
          st.x += st.d; // gentle drift
          if (st.x > w + 2) st.x = -2;
        }
        // shooting stars (rarer on mobile to keep 60fps)
        nextMeteor -= 16;
        if (nextMeteor <= 0) { spawnMeteor(); nextMeteor = (isMobile ? 6500 : 2800) + Math.random() * 4500; }
        for (let i = meteors.length - 1; i >= 0; i--) {
          const m = meteors[i];
          m.x += m.vx; m.y += m.vy; m.life -= 0.016;
          if (m.life <= 0 || m.x < -m.len || m.y > h + m.len) { meteors.splice(i, 1); continue; }
          const tailX = m.x - m.vx * (m.len / 10);
          const tailY = m.y - m.vy * (m.len / 10);
          const grad = ctx.createLinearGradient(m.x, m.y, tailX, tailY);
          grad.addColorStop(0, `rgba(230,240,255,${0.85 * m.life})`);
          grad.addColorStop(1, 'rgba(230,240,255,0)');
          ctx.globalAlpha = 1;
          ctx.strokeStyle = grad;
          ctx.lineWidth = 1.6;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(m.x, m.y);
          ctx.lineTo(tailX, tailY);
          ctx.stroke();
          // bright head
          ctx.fillStyle = `rgba(255,255,255,${0.9 * m.life})`;
          ctx.beginPath();
          ctx.arc(m.x, m.y, 1.6, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalAlpha = 1;
        raf = requestAnimationFrame(tick);
      };
      const io = new IntersectionObserver(([e]) => {
        visible = e.isIntersecting;
        if (visible && !raf) raf = requestAnimationFrame(tick);
      });
      io.observe(canvas);
      let rt;
      window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(resize, 150); }, { passive: true });
      resize();
    });
  };

  /* ---------- Pointer tilt ---------- */
  const initTilt = () => {
    if (reduceMotion || N.settings.tilt === false) return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    $$('[data-tilt]').forEach((el) => {
      const parent = el.closest('[data-tilt-area]') || el.parentElement;
      let raf = null; let rx = 0; let ry = 0;
      parent.addEventListener('pointermove', (e) => {
        const rect = parent.getBoundingClientRect();
        ry = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
        rx = ((e.clientY - rect.top) / rect.height - 0.5) * -8;
        if (!raf) raf = requestAnimationFrame(() => {
          el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)`;
          raf = null;
        });
      });
      parent.addEventListener('pointerleave', () => {
        el.style.transform = '';
      });
    });
  };

  /* ---------- Count-up stats ---------- */
  const initCounters = () => {
    const els = $$('[data-count-to]');
    if (!els.length || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        io.unobserve(e.target);
        const el = e.target;
        const target = parseFloat(el.dataset.countTo);
        const decimals = (el.dataset.countTo.split('.')[1] || '').length;
        if (reduceMotion) { el.textContent = el.dataset.countTo; return; }
        const dur = 1400; const start = performance.now();
        const step = (now) => {
          const p = Math.min((now - start) / dur, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = (target * eased).toFixed(decimals);
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }, { threshold: 0.6 });
    els.forEach((el) => io.observe(el));
  };

  /* ---------- Recently viewed (localStorage) ---------- */
  const initRecentlyViewed = () => {
    const KEY = 'nocturne:recent';
    const current = $('[data-product-handle]');
    let list = [];
    try { list = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { list = []; }
    if (current) {
      const handle = current.dataset.productHandle;
      list = [handle, ...list.filter((h) => h !== handle)].slice(0, 8);
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) { /* private mode */ }
    }
    const holder = $('[data-recently-viewed]');
    if (!holder) return;
    const exclude = holder.dataset.exclude;
    const handles = list.filter((h) => h !== exclude).slice(0, 4);
    if (!handles.length) { holder.closest('.section')?.remove(); return; }
    Promise.all(handles.map((h) => fetch(`/products/${h}?view=card`).then((r) => (r.ok ? r.text() : '')).catch(() => '')))
      .then((cards) => {
        const html = cards.filter(Boolean).join('');
        if (!html) { holder.closest('.section')?.remove(); return; }
        holder.innerHTML = html;
      });
  };

  /* ---------- Quantity steppers (generic) ---------- */
  const initQty = () => {
    document.addEventListener('click', (e) => {
      const plus = e.target.closest('[data-qty-step]');
      if (!plus) return;
      const wrap = plus.closest('.qty');
      if (!wrap || plus.closest('[data-cart-line]')) return;
      const input = $('input', wrap);
      const step = Number(plus.dataset.qtyStep);
      input.value = Math.max(1, (Number(input.value) || 1) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  /* ---------- Certificate showcase tabs + lightbox ---------- */
  const initCertShowcase = () => {
    $$('[data-cert-showcase]').forEach((wrap) => {
      const tabs = $$('.cert-tab', wrap);
      const slides = $$('.cert-showcase__slide', wrap);
      tabs.forEach((tab, i) => tab.addEventListener('click', () => {
        tabs.forEach((t, j) => { t.classList.toggle('is-active', i === j); t.setAttribute('aria-selected', i === j); });
        slides.forEach((s, j) => s.classList.toggle('is-active', i === j));
      }));
    });
    // Lightbox
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-lightbox-src]');
      if (!trigger) return;
      const overlay = document.createElement('div');
      overlay.className = 'lightbox';
      overlay.innerHTML = '<button class="lightbox__close" aria-label="Close">&times;</button><img alt="">';
      overlay.querySelector('img').src = trigger.dataset.lightboxSrc;
      document.body.appendChild(overlay);
      document.body.classList.add('drawer-open');
      requestAnimationFrame(() => overlay.classList.add('is-visible'));
      const close = () => {
        overlay.classList.remove('is-visible');
        document.body.classList.remove('drawer-open');
        setTimeout(() => overlay.remove(), 250);
        document.removeEventListener('keydown', onKey);
      };
      const onKey = (ev) => { if (ev.key === 'Escape') close(); };
      overlay.addEventListener('click', close);
      document.addEventListener('keydown', onKey);
    });
  };

  /* ---------- Sales notification popups ---------- */
  const initSalesPop = () => {
    const src = $('[data-sales-pop]');
    if (!src) return;
    if (src.hasAttribute('data-desktop-only') && window.innerWidth < 750) return;
    let entries = [];
    try { entries = JSON.parse(src.textContent); } catch (e) { return; }
    entries = entries.filter((e) => e && e.title);
    if (!entries.length) return;
    const firstDelay = Number(src.dataset.firstDelay) * 1000;
    const interval = Number(src.dataset.interval) * 1000;
    const maxShows = Number(src.dataset.maxShows);
    const minM = Number(src.dataset.minMinutes);
    const maxM = Number(src.dataset.maxMinutes);
    let shows = 0;
    let order = [...entries].sort(() => Math.random() - 0.5);

    const showOne = () => {
      if (shows >= maxShows || document.hidden) return schedule();
      const e = order[shows % order.length];
      shows += 1;
      const mins = minM + Math.floor(Math.random() * Math.max(1, maxM - minM));
      const pop = document.createElement('a');
      pop.className = 'sales-pop';
      pop.href = e.url;
      pop.setAttribute('role', 'status');
      pop.innerHTML =
        '<span class="sales-pop__media">' + (e.image ? '<img src="' + e.image + '" alt="" width="52" height="52" loading="lazy">' : '') + '</span>' +
        '<span class="sales-pop__body">' +
          '<span class="sales-pop__who"></span>' +
          '<span class="sales-pop__what"></span>' +
          '<span class="sales-pop__meta">' + mins + ' minutes ago · <em>Verified order</em></span>' +
        '</span>' +
        '<button class="sales-pop__close" type="button" aria-label="Dismiss">&times;</button>';
      pop.querySelector('.sales-pop__who').textContent = (e.name || 'Someone') + (e.city ? ' from ' + e.city : '') + ' ordered';
      pop.querySelector('.sales-pop__what').textContent = e.title;
      document.body.appendChild(pop);
      requestAnimationFrame(() => pop.classList.add('is-visible'));
      const remove = () => { pop.classList.remove('is-visible'); setTimeout(() => pop.remove(), 400); };
      pop.querySelector('.sales-pop__close').addEventListener('click', (ev) => { ev.preventDefault(); ev.stopPropagation(); shows = maxShows; remove(); });
      setTimeout(remove, 6500);
      schedule();
    };
    const schedule = () => {
      if (shows >= maxShows) return;
      setTimeout(showOne, interval + Math.random() * 6000);
    };
    setTimeout(showOne, firstDelay);
  };

  /* ---------- Live inventory note on variant switch (PDP) ---------- */
  const initInventoryNote = () => {
    const note = $('[data-inv-note]');
    if (!note) return;
    const label = $('[data-inv-label]', note);
    const bar = $('[data-inv-bar]');
    const update = (qty, managed) => {
      if (!managed || qty === null || isNaN(qty)) return;
      if (qty <= 0) return;
      label.textContent = qty <= 20 ? ('Only ' + qty + ' boxes left in stock — order soon') : (qty + ' boxes in stock · ships within 24 hours');
      note.classList.toggle('inventory-note--low', qty <= 20);
      if (bar) {
        const pct = Math.max(6, Math.min(100, Math.round((qty / 50) * 100)));
        bar.style.width = pct + '%';
        bar.classList.toggle('is-low', qty <= 20);
      }
    };
    // hook into pack selection
    document.addEventListener('click', (e) => {
      const opt = e.target.closest('.pack-option[data-inv]');
      if (!opt) return;
      update(Number(opt.dataset.inv), opt.dataset.invManaged === 'true');
    });
  };

  /* ---------- Sale countdown (14 Aug etc.) ---------- */
  const initSaleTimers = () => {
    const timers = $$('[data-sale-countdown]');
    if (!timers.length) return;
    timers.forEach((el) => {
      const end = new Date(el.dataset.saleEnd).getTime();
      if (Number.isNaN(end)) return;
      const h = $('[data-t-h]', el);
      const m = $('[data-t-m]', el);
      const s = $('[data-t-s]', el);
      const area = el.closest('[data-sale-area]') || el;
      const pad = (n) => String(n).padStart(2, '0');
      let iv;
      const tick = () => {
        const d = end - Date.now();
        if (d <= 0) {
          area.style.display = 'none';
          if (iv) clearInterval(iv);
          return;
        }
        if (h) h.textContent = pad(Math.floor(d / 36e5));
        if (m) m.textContent = pad(Math.floor((d % 36e5) / 6e4));
        if (s) s.textContent = pad(Math.floor((d % 6e4) / 1000));
      };
      tick();
      iv = setInterval(tick, 1000);
    });
  };

  /* ---------- Boot ---------- */
  const boot = () => {
    document.body.classList.remove('no-js-body');
    initReveal();
    initHeader();
    initAnnouncement();
    initDrawers();
    initProductForms();
    initPackSelectors();
    initGallery();
    initStickyAtc();
    initStarfields();
    initTilt();
    initCounters();
    initRecentlyViewed();
    initQty();
    initCertShowcase();
    initSalesPop();
    initInventoryNote();
    initSaleTimers();
    bindCartLines();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
